import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  alpha,
} from '@mui/material';
import {
  ExpandMore,
  Twitter,
  Email,
} from '@mui/icons-material';
import { GradientCard, GlassCard, FeatureCard } from '../../components/v2';
import { gradients } from '../../theme/v2Theme';

export const AboutPage: React.FC = () => {
  const [expanded, setExpanded] = useState<string | false>('faq1');

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const faqs = [
    {
      id: 'faq1',
      question: 'What is addrs?',
      answer:
        'addrs is a free, non-custodial platform that lets you create a simple payment profile for all your cryptocurrency addresses. Instead of sharing long wallet addresses, you can share one simple link like addrs.to/yourhandle.',
    },
    {
      id: 'faq2',
      question: 'What is the .pay.eth ENS feature?',
      answer:
        'When you add an Ethereum (ETH) address to your profile, you automatically get a free ENS address in the format yourhandle.pay.eth! This works for all your ETH and ERC20 token addresses, making them much more memorable and easy to share. Note: This ENS feature is currently in beta and may change.',
    },
    {
      id: 'faq3',
      question: 'Is addrs free to use?',
      answer:
        'Yes! addrs is completely free to use. There are no subscription fees, no transaction fees, and no hidden costs. Create your profile, add addresses, and share your link—all for free.',
    },
    {
      id: 'faq4',
      question: 'Is it safe? Do you have access to my funds?',
      answer:
        'addrs is 100% non-custodial, meaning we never have access to your funds or private keys. We only store your public wallet addresses. All payments go directly to your wallets—we never touch your crypto.',
    },
    {
      id: 'faq5',
      question: 'Which cryptocurrencies are supported?',
      answer:
        'We support 32+ cryptocurrencies including Bitcoin (BTC), Ethereum (ETH), and all major tokens like USDC, USDT, MATIC, SOL, BNB, and more. We regularly add support for new chains and tokens.',
    },
    {
      id: 'faq6',
      question: 'Can I accept fiat payments (USD, EUR, etc.)?',
      answer:
        'Yes! Through our integrated fiat-to-crypto providers (Transak, Wyre, Coinbase), your customers can pay with credit cards, debit cards, or bank transfers. The funds are automatically converted to crypto and sent to your wallet.',
    },
    {
      id: 'faq7',
      question: 'How do I create a payment link without an account?',
      answer:
        'Visit the "Create Payment Request" page, select your cryptocurrency, paste your wallet address, optionally set an amount, and click generate. You\'ll get a shareable payment link instantly—no account required!',
    },
    {
      id: 'faq8',
      question: 'Can I embed my addrs profile on my website?',
      answer:
        'Absolutely! We provide embed codes that you can copy and paste into your website. Your addrs profile will display beautifully on any page using our dedicated embed.addrs.to subdomain with a clean, header-free layout perfect for iframes.',
    },
    {
      id: 'faq9',
      question: 'How do QR codes work?',
      answer:
        'Every address on your profile has a QR code that can be scanned with any crypto wallet app. This makes in-person payments quick and easy—just show the QR code and let them scan!',
    },
  ];

  const embedCode = `<iframe
  src="https://embed.addrs.to/yourhandle"
  width="100%"
  height="600"
  frameborder="0"
  style="border-radius: 16px;"
></iframe>`;

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <Box
        sx={{
          background: gradients.mesh,
          py: 10,
          borderRadius: '0 0 60px 60px',
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" position="relative" zIndex={1}>
            <Typography
              variant="h2"
              fontWeight={800}
              sx={{
                color: '#fff',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
              }}
            >
              About addrs
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: alpha('#fff', 0.9), maxWidth: 700, mx: 'auto' }}
            >
              Learn everything about how addrs works and how you can start accepting crypto
              payments today
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ pb: 10 }}>
        {/* What is addrs */}
        <Box sx={{ mb: 10 }}>
          <Typography variant="h4" fontWeight={800} gutterBottom textAlign="center">
            What is <span style={{ background: gradients.primary, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>addrs</span>?
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            maxWidth={800}
            mx="auto"
            sx={{ mb: 6, lineHeight: 1.8 }}
          >
            addrs is like Linktree for crypto payments. Create one simple profile with all your
            cryptocurrency addresses, then share a single link. Perfect for creators, freelancers,
            businesses, and anyone who accepts crypto.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <FeatureCard
                icon="🎯"
                title="Simple & Clean"
                description="One link for all your crypto addresses. No more copying and pasting long wallet addresses."
                gradient={gradients.primary}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FeatureCard
                icon="🔒"
                title="Non-Custodial"
                description="You own your addresses. We never hold your funds or access your private keys."
                gradient={gradients.success}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FeatureCard
                icon="⚡"
                title="Fast & Free"
                description="Set up in 2 minutes. No fees, no subscriptions. Completely free forever."
                gradient={gradients.ocean}
              />
            </Grid>
          </Grid>
        </Box>

        {/* How it Works */}
        <Box sx={{ mb: 10 }}>
          <Typography variant="h4" fontWeight={800} gutterBottom textAlign="center" sx={{ mb: 6 }}>
            How It Works
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                step: '1',
                title: 'Create Your Handle',
                description: 'Sign up and claim your unique handle like @yourname',
              },
              {
                step: '2',
                title: 'Add Your Addresses',
                description: 'Add wallet addresses for all the cryptocurrencies you accept',
              },
              {
                step: '3',
                title: 'Share Your Link',
                description: 'Share addrs.to/yourname and start receiving payments',
              },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <GlassCard sx={{ p: 4, textAlign: 'center', height: '100%' }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      background: gradients.primary,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2rem',
                      fontWeight: 800,
                      mx: 'auto',
                      mb: 3,
                    }}
                  >
                    {item.step}
                  </Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* FAQs */}
        <Box sx={{ mb: 10 }}>
          <Typography variant="h4" fontWeight={800} gutterBottom textAlign="center" sx={{ mb: 6 }}>
            Frequently Asked Questions
          </Typography>

          <Box maxWidth={900} mx="auto">
            {faqs.map((faq) => (
              <Accordion
                key={faq.id}
                expanded={expanded === faq.id}
                onChange={handleChange(faq.id)}
                sx={{
                  background: '#ffffff',
                  borderRadius: '16px !important',
                  mb: 2,
                  border: '2px solid',
                  borderColor: alpha('#6366f1', 0.1),
                  boxShadow: '0px 4px 16px rgba(99, 102, 241, 0.08)',
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    m: '0 0 16px 0',
                    borderColor: '#6366f1',
                    boxShadow: '0px 8px 32px rgba(99, 102, 241, 0.16)',
                  },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" fontWeight={700}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" color="text.secondary" lineHeight={1.8}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        {/* Embed Code */}
        <Box sx={{ mb: 10 }}>
          <Typography variant="h4" fontWeight={800} gutterBottom textAlign="center" sx={{ mb: 6 }}>
            Embed on Your Website
          </Typography>

          <Grid container spacing={4} maxWidth={1000} mx="auto">
            <Grid item xs={12} md={6}>
              <GlassCard sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Copy Embed Code
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Paste this code into your website's HTML to embed your addrs profile. Uses embed.addrs.to for a clean, header-free display.
                </Typography>
                <Box
                  sx={{
                    background: alpha('#000', 0.05),
                    p: 2,
                    borderRadius: 1.5,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: alpha('#000', 0.1),
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{embedCode}</pre>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 2,
                    background: gradients.primary,
                    '&:hover': {
                      background: gradients.primary,
                      opacity: 0.9,
                    },
                  }}
                  onClick={() => navigator.clipboard.writeText(embedCode)}
                >
                  Copy Code
                </Button>
              </GlassCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassCard sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  WordPress Shortcode
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  If you're using WordPress, you can use this shortcode instead
                </Typography>
                <Box
                  sx={{
                    background: alpha('#000', 0.05),
                    p: 2,
                    borderRadius: 1.5,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: alpha('#000', 0.1),
                  }}
                >
                  [addrs handle="yourhandle"]
                </Box>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => navigator.clipboard.writeText('[addrs handle="yourhandle"]')}
                >
                  Copy Shortcode
                </Button>
              </GlassCard>
            </Grid>
          </Grid>
        </Box>

        {/* Contact */}
        <Box>
          <GradientCard
            sx={{
              p: 6,
              textAlign: 'center',
              background: gradients.mesh,
            }}
          >
            <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color: '#fff' }}>
              Still Have Questions?
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, opacity: 0.95, color: '#fff' }}>
              Get in touch with us on social media or via email
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<Twitter />}
                href="http://twitter.com/not_pr0"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  background: '#ffffff',
                  color: '#6366f1',
                  fontWeight: 700,
                  '&:hover': {
                    background: alpha('#ffffff', 0.9),
                  },
                }}
              >
                Twitter
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Email />}
                href="mailto:hey@addrs.to"
                sx={{
                  borderColor: '#ffffff',
                  color: '#ffffff',
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: '#ffffff',
                    background: alpha('#ffffff', 0.1),
                  },
                }}
              >
                Email Us
              </Button>
            </Stack>
          </GradientCard>
        </Box>
      </Container>
    </Box>
  );
};
